
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { QrCode, Share2, Copy, Users, Trophy, Gift } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const ReferralSystemV2 = () => {
  const [referralCode, setReferralCode] = useState('');

  // Generate referral code on component mount
  React.useEffect(() => {
    const generateCode = () => {
      const code = Math.random().toString(36).substring(2, 8).toUpperCase();
      setReferralCode(code);
    };
    generateCode();
  }, []);

  // Fetch user's referrals
  const { data: referrals = [] } = useQuery({
    queryKey: ['user-referrals'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('referrals')
        .select('*')
        .eq('referrer_id', user.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    }
  });

  const totalEarned = referrals
    .filter(r => r.status === 'completed')
    .reduce((sum, r) => sum + (r.credit_amount || 0), 0);

  const referralLink = `${window.location.origin}/refer/${referralCode}`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(referralLink);
    toast.success('Referral link copied to clipboard!');
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent('Join me at JUV and get $50 in credits!');
    const body = encodeURIComponent(
      `Hey! I've been loving my treatments at JUV and thought you'd be interested too. Use my referral link to get $50 in treatment credits when you sign up: ${referralLink}`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const shareViaSMS = () => {
    const message = encodeURIComponent(
      `Join me at JUV! Use my link to get $50 in treatment credits: ${referralLink}`
    );
    window.open(`sms:?body=${message}`);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold text-juv-deep-chestnut">
          Refer & Earn
        </h1>
        <p className="text-lg text-juv-deep-chestnut/70">
          Share the glow and earn credits for every friend who joins
        </p>
      </div>

      {/* Earnings Summary */}
      <Card className="border-juv-terracotta/30 bg-gradient-to-r from-juv-soft-blush to-white">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-juv-terracotta">{referrals.length}</div>
              <div className="text-sm text-juv-deep-chestnut/70">Total Referrals</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-juv-terracotta">
                {referrals.filter(r => r.status === 'completed').length}
              </div>
              <div className="text-sm text-juv-deep-chestnut/70">Successful</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-juv-terracotta">${totalEarned}</div>
              <div className="text-sm text-juv-deep-chestnut/70">Credits Earned</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Referral Link & QR Code */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-juv-rose-clay/20 bg-white">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-juv-deep-chestnut">
              <Share2 className="h-5 w-5" />
              <span>Your Referral Link</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-juv-soft-blush rounded-lg">
              <p className="text-sm text-juv-deep-chestnut break-all">{referralLink}</p>
            </div>
            
            <div className="grid grid-cols-1 gap-2">
              <Button
                onClick={copyToClipboard}
                className="bg-juv-terracotta hover:bg-juv-deep-chestnut text-white"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy Link
              </Button>
              <Button
                onClick={shareViaEmail}
                variant="outline"
                className="text-juv-terracotta border-juv-terracotta hover:bg-juv-soft-blush"
              >
                Share via Email
              </Button>
              <Button
                onClick={shareViaSMS}
                variant="outline"
                className="text-juv-terracotta border-juv-terracotta hover:bg-juv-soft-blush"
              >
                Share via Text
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-juv-rose-clay/20 bg-white">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-juv-deep-chestnut">
              <QrCode className="h-5 w-5" />
              <span>QR Code</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-center">
              <div className="w-48 h-48 bg-juv-soft-blush rounded-lg flex items-center justify-center">
                <QrCode className="h-24 w-24 text-juv-terracotta" />
              </div>
            </div>
            <p className="text-sm text-juv-deep-chestnut/70 text-center mt-4">
              Friends can scan this QR code to join with your referral
            </p>
          </CardContent>
        </Card>
      </div>

      {/* How It Works */}
      <Card className="border-juv-rose-clay/20 bg-white">
        <CardHeader>
          <CardTitle className="text-juv-deep-chestnut">How It Works</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-juv-terracotta rounded-full flex items-center justify-center mx-auto">
                <span className="text-white font-bold">1</span>
              </div>
              <h3 className="font-semibold text-juv-deep-chestnut">Share Your Link</h3>
              <p className="text-sm text-juv-deep-chestnut/70">
                Send your unique referral link to friends
              </p>
            </div>
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-juv-terracotta rounded-full flex items-center justify-center mx-auto">
                <span className="text-white font-bold">2</span>
              </div>
              <h3 className="font-semibold text-juv-deep-chestnut">Friend Signs Up</h3>
              <p className="text-sm text-juv-deep-chestnut/70">
                They join JUV and get their first treatment
              </p>
            </div>
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-juv-terracotta rounded-full flex items-center justify-center mx-auto">
                <span className="text-white font-bold">3</span>
              </div>
              <h3 className="font-semibold text-juv-deep-chestnut">You Both Earn</h3>
              <p className="text-sm text-juv-deep-chestnut/70">
                $50 credits for you, $50 credits for them
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Referral History */}
      <Card className="border-juv-rose-clay/20 bg-white">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2 text-juv-deep-chestnut">
            <Users className="h-5 w-5" />
            <span>Referral History</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {referrals.length > 0 ? (
            <div className="space-y-4">
              {referrals.map((referral) => (
                <div key={referral.id} className="flex items-center justify-between p-4 border border-juv-rose-clay/20 rounded-lg">
                  <div>
                    <p className="font-medium text-juv-deep-chestnut">
                      {referral.referred_email || 'Email not provided'}
                    </p>
                    <p className="text-sm text-juv-deep-chestnut/70">
                      Referred {new Date(referral.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <Badge 
                      variant={referral.status === 'completed' ? 'default' : 'outline'}
                      className={referral.status === 'completed' ? 'bg-green-100 text-green-700' : ''}
                    >
                      {referral.status}
                    </Badge>
                    {referral.status === 'completed' && (
                      <p className="text-sm text-green-600 font-medium mt-1">
                        +${referral.credit_amount} earned
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Gift className="h-12 w-12 text-juv-terracotta mx-auto mb-4" />
              <p className="text-juv-deep-chestnut/70">No referrals yet</p>
              <p className="text-sm text-juv-deep-chestnut/50">
                Start sharing your link to earn credits!
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ReferralSystemV2;
